import { AddressData } from '@/common/types/address'
import { BankInstitution } from '@/common/types/bankInstitution.type'
import { BillingCaptureMethod } from '@/common/types/establishment/billingCaptureMethod'
import { EstablishmentInfrastructureData } from '@/common/types/establishmentInfrastructure'
import { EstablishmentRequiredDocumentData } from '@/common/types/establishmentRequiredDocument'
import { EstablishmentTechnicalProfessionalData } from '@/common/types/establishmentTechnicalProfessional'
import { File } from '@/common/types/file'
import { LogType } from '@/common/types/lead/log.type'
import { MetaResponse } from '@/common/types/metaResponse'
import { ProductData } from '@/common/types/product.type'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { UserData } from '@/common/types/users'
import { stateStrategy } from '@/features/establishmentLeadsFunnel/constants/stateStrategy.constant'
import { MaintenanceObjectiveActionsData } from '@/features/maintenance/objectiveMaintenance/objectiveActions/types/MaintenanceObjectiveActions.type'
import { MaintenanceObjectivePartsData } from '@/features/maintenance/objectiveMaintenance/objectiveParts/types/MaintenanceObjectiveParts.type'
import { MaintenanceObjectiveSystemData } from '@/features/maintenance/objectiveMaintenance/objectiveSystems/types/MaintenanceObjectiveSystem.type'
import { PosData } from '@/features/pos/types/pos.type'
import {
  ContactData,
  ContractFeeData,
  Cycle,
  LastReview,
  LastStateChangeLog,
  RequirementListSummaryData,
} from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLead'

export enum MaintenanceSpecificationDirectiveKlass {
  DiagnosticEntry = 'Maintenance::Directive::DiagnosticEntry',
  ExecutionEntry = 'Maintenance::Directive::ExecutionEntry',
  ScrapEntry = 'Maintenance::Directive::ScrapEntry',
}

export type AccreditationInformation = {
  accreditation_information_capture_methods: {
    id: string
    capture_method: BillingCaptureMethod
  }[]
  accreditation_information_product_categories: {
    id: string
    product_category: ProductCategoryData
  }[]
  last_review?: LastReview
  photos: {
    id: string
    attachment_id: string
    url: string
    content_type: string
  }[]
  accreditation_information_pos_machines: {
    id: string
    machine: PosData
  }[],
  authorizer_identifier?: string,
  payment_advance_daily_interest?: string
}

export type GeneralInformationData = {
  cnpj?: string
  email?: string
  phone: {
    number: string
  }
  fantasy_name?: string
  official_name?: string
  primary_cnae?: string
  score?: string
  simple_national_optant?: boolean
  state_inscription?: string
  commercial_kind?: string
  company_logo?: {
    url: string
    content_type: string
  }
  flag?: {
    id: string
    display_name: string
  }
  website?: string
  last_review?: LastReview
}

export type ContractData = {
  id: string
  cycle?: Cycle
  cycle_start_day?: number
  closing_to_due_in_days?: number
  contract_fees?: ContractFeeData[]
  contract_product_categories?: {
    id: string
    product_category: ProductCategoryData
  }[]
  contract_products?: {
    id: string
    product: ProductData
  }[]
  file: {
    id?: string
    attachment_id?: string
    content_type?: string
    url?: string
  }
  last_review?: LastReview
  protocol_number?: string
  state: string
}

export type CaptureMethod = {
  id: string
  description: string
  display_name: string
  perma_name: string
}

export type ClientConsumptionData = {
  id: string
  status: string
  capture_method: CaptureMethod
  total_amount: string
  card_last_four_digits: string
  protocol_number?: number
}

export type BankAccountInformation = {
  account?: string
  agency?: string
  bank_institution?: BankInstitution
  payee_name?: string
  payee_cnpj?: string
}

export type EstablishmentLeadData = {
  id: string
  accreditation_information: AccreditationInformation
  authorizer_identifier: string
  address?: AddressData & { last_review?: LastReview }
  commercial_manager: UserData
  commercial_manager_substitute: UserData
  contact_for_master_user?: ContactData
  contacts?: ContactData[]
  contract?: ContractData
  first_complete_consumption_per_capture_method: ClientConsumptionData[]
  creator: UserData
  display_name: string
  general_information: GeneralInformationData
  generic_documents: {
    id: string
    attachment_id: string
    content_type: string
    url: string
  }[]
  bank_account_information: BankAccountInformation
  last_review?: LastReview
  logs: LogType[]
  observations: string
  requirement_list_summary: RequirementListSummaryData
  shipment?: {
    status?: string
  }
  state: keyof typeof stateStrategy
  created_at: string
  archived_at?: string
  last_state_change_log?: LastStateChangeLog
  ca_module_enabled: boolean;
  cm_module_enabled: boolean;
  tow_service_available: boolean;
  maintenance_infrastructures: {
    id: string;
    company_id: string | null;
    infrastructure: EstablishmentInfrastructureData;
    infrastructure_id: string;
    lead_id: string;
  }[];
  maintenance_technical_professionals: {
    id: string;
    company_id: string | null;
    lead_id: string;
    technical_professional: EstablishmentTechnicalProfessionalData;
    technical_professional_id: string;
  }[];
  maintenance_specialty: {
    id: string;
    action: MaintenanceObjectiveActionsData;
    company_id: string | null;
    lead_id: string;
    system_part: {
      id: string;
      part: MaintenanceObjectivePartsData;
      system: MaintenanceObjectiveSystemData;
    };
  } | null;
  maintenance_required_documents: {
    id: string;
    company_id: string | null;
    document_id: string;
    document: EstablishmentRequiredDocumentData;
    file: File;
    lead_id: string;
    status: string;
    valid_until: string;
    issue_date: string;
  }[];
  maintenance_specifications: {
    id: string;
    company_id: string | null;
    directive_klass: MaintenanceSpecificationDirectiveKlass;
    equipment_category_specifications: {
      id: string;
      category: ProductCategoryData;
      category_id: string;
      specification_id: string;
    }[];
    equipment_manufacturer_specifications: {
      id: string;
      manufacturer: {
        id: string;
        display_name: string;
      }
    }[];
    lead_id: string;
    system_specifications: {
      id: string;
      specification_id: string;
      system: MaintenanceObjectiveSystemData;
      system_id: string;
    }[];
  }[];
  maintenance_contract: ContractData;
}

export type EstablishmentLeadsResponse = {
  data: {
    data: EstablishmentLeadData[]
    meta: MetaResponse
  }
}

export type EstablishmentLeadResponse = {
  data: {
    data: EstablishmentLeadData
  }
}

export type EstablishmentLeadTransformerResponse = {
  data: {
    registered?: EstablishmentLeadData[]
    qualifying?: EstablishmentLeadData[]
    deploying?: EstablishmentLeadData[]
  }
  meta: MetaResponse
}
